import React, { useState, useEffect } from 'react';
import { getHours } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { FileText, Filter } from 'lucide-react';

function StatusBadge({ status }) {
  const styles = {
    pending: 'bg-[#D4A017]/10 text-[#D4A017] border border-[#D4A017]/20',
    approved: 'bg-[#2C5545]/10 text-[#2C5545] border border-[#2C5545]/20',
    rejected: 'bg-[#B74134]/10 text-[#B74134] border border-[#B74134]/20',
  };
  return (
    <span className={`px-3 py-1 rounded-full text-xs font-bold ${styles[status] || styles.pending}`} data-testid={`status-badge-${status}`}>
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
}

export default function MyHoursPage() {
  const { user } = useAuth();
  const [hours, setHours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    loadHours();
  }, [statusFilter]);

  const loadHours = async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      const { data } = await getHours(params);
      setHours(data.hours || []);
    } catch (e) {
      console.error('Failed to load hours', e);
    } finally {
      setLoading(false);
    }
  };

  const isAdmin = user?.role === 'admin' || user?.role === 'supervisor';

  return (
    <div className="animate-fade-in" data-testid="my-hours-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8 gap-4">
        <div>
          <h1 className="font-['Manrope'] text-3xl font-bold text-[#2D2B2A] tracking-tight">
            {isAdmin ? 'All Hours' : 'My Hours'}
          </h1>
          <p className="text-[#5C5855] mt-1">
            {isAdmin ? 'View all submitted hours' : 'View your submitted hours and their status'}
          </p>
        </div>

        <div className="flex items-center gap-3">
          <Filter className="h-4 w-4 text-[#5C5855]" />
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger data-testid="status-filter" className="w-[160px] border-[#D1C9C0] bg-white">
              <SelectValue placeholder="Filter status" />
            </SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="w-full rounded-xl border border-[#E8E3DD] overflow-hidden bg-white">
        <Table>
          <TableHeader>
            <TableRow className="bg-[#FAF8F5] border-b border-[#E8E3DD]">
              <TableHead className="text-[#5C5855] font-semibold text-xs uppercase tracking-wider px-6 py-4">Date</TableHead>
              {isAdmin && <TableHead className="text-[#5C5855] font-semibold text-xs uppercase tracking-wider px-6 py-4">Volunteer</TableHead>}
              <TableHead className="text-[#5C5855] font-semibold text-xs uppercase tracking-wider px-6 py-4">Service Type</TableHead>
              <TableHead className="text-[#5C5855] font-semibold text-xs uppercase tracking-wider px-6 py-4">Hours</TableHead>
              <TableHead className="text-[#5C5855] font-semibold text-xs uppercase tracking-wider px-6 py-4">Status</TableHead>
              <TableHead className="text-[#5C5855] font-semibold text-xs uppercase tracking-wider px-6 py-4">Description</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={i} className="border-b border-[#E8E3DD]">
                  <TableCell colSpan={isAdmin ? 6 : 5} className="px-6 py-4">
                    <div className="h-4 bg-[#F2EFEB] rounded animate-pulse" />
                  </TableCell>
                </TableRow>
              ))
            ) : hours.length === 0 ? (
              <TableRow>
                <TableCell colSpan={isAdmin ? 6 : 5} className="text-center py-12 text-[#8A847F]">
                  <FileText className="h-8 w-8 mx-auto mb-2 opacity-40" />
                  <p className="text-sm">No hours found</p>
                </TableCell>
              </TableRow>
            ) : (
              hours.map((log) => (
                <TableRow key={log.log_id} className="border-b border-[#E8E3DD] hover:bg-[#F2EFEB]/50 transition-colors" data-testid={`hour-row-${log.log_id}`}>
                  <TableCell className="px-6 py-4 text-sm text-[#2D2B2A] font-medium">{log.date}</TableCell>
                  {isAdmin && <TableCell className="px-6 py-4 text-sm text-[#2D2B2A]">{log.volunteer_name}</TableCell>}
                  <TableCell className="px-6 py-4 text-sm text-[#2D2B2A]">{log.service_type}</TableCell>
                  <TableCell className="px-6 py-4 text-sm text-[#2D2B2A] font-semibold">{log.hours}</TableCell>
                  <TableCell className="px-6 py-4"><StatusBadge status={log.status} /></TableCell>
                  <TableCell className="px-6 py-4 text-sm text-[#5C5855] max-w-[200px] truncate">{log.description || '—'}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
